import React, { useEffect, useState, Fragment } from 'react';
import { compose } from 'recompose';
import { reduxForm, getFormValues, Field } from 'redux-form';
import { connect } from 'react-redux';

import {  TextInput, SelectInput, CardElement, StatusChip, EmptyState } from '../../_Components';

import AssignField from '../components/AssignField';
import { UPDATE, CREATE, provider } from '../../_utils';

import { AddButton, BlockButton } from '../../_Components';

const rows = ['Пн.', 'Вт.', 'Ср.', 'Чт.', 'Пт.', 'Сб.', 'Вс.'];

const padTime = (minutes) => {
    const total = Number(minutes);
    if (Number.isNaN(total)) {
        return '';
    }
    const hour = Math.floor(total / 60);
    const mins = total % 60;
    return `${String(hour).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const SHIFT_TIMES = (() => {
    const list = [];
    for (let t = 7 * 60; t <= 24 * 60; t += 30) {
        list.push({ id: t, name: padTime(t) });
    }
    return list;
})();

const HOURS = Array.from({ length: 17 }, (_, i) => i + 7);

const formatMinutes = (value) => {
    if (value === undefined || value === null || value === '') {
        return '';
    }
    return padTime(value);
};

const hourOverlapsShift = (hour, daySchedule) => {
    if (!daySchedule) {
        return false;
    }
    const start = Number(daySchedule.start);
    const end = Number(daySchedule.end);
    if (Number.isNaN(start) || Number.isNaN(end)) {
        return false;
    }
    const hourStart = hour * 60;
    const hourEnd = (hour + 1) * 60;
    return hourStart < end && hourEnd > start;
};

const scheduleSummary = (campaign) => {
    const day = campaign && campaign.schedule && campaign.schedule[1];
    if (!day) {
        return null;
    }
    const start = formatMinutes(day.start);
    const end = formatMinutes(day.end);
    if (!start || !end) {
        return null;
    }
    return `пн–вс · ${start}–${end}`;
};

const CAMPAIGN_TYPES = [
    { id: 'Inbound', name: 'Входящие', hint: 'Входящие очереди КЦ' },
    { id: 'Outbound', name: 'Исходящие', hint: 'Исходящие кампании КЦ' },
    { id: 'Finance', name: 'Финансовые', hint: 'Финансовые линии' },
];

const Icon = ({ d }) => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const CreateCompanyModal = ({ open, onClose, onSave }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState('Inbound');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        setName('');
        setType('Inbound');
        setSaving(false);
        return undefined;
    }, [open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    const canSave = Boolean(name.trim()) && Boolean(type) && !saving;

    const handleSave = async () => {
        if (!canSave) {
            return;
        }
        setSaving(true);
        try {
            await onSave({ name: name.trim(), type });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="pc-emp-ov" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
            <div className="pc-emp-modal pc-emp-modal--sm" role="dialog" aria-modal="true" aria-labelledby="pc-company-create-title">
                <div className="pc-emp-mh">
                    <div>
                        <h3 id="pc-company-create-title">Новая компания</h3>
                        <div className="sub">Расписание, операторы и очереди — на карточке компании</div>
                    </div>
                    <button type="button" className="pc-emp-btn pc-emp-btn--ico pc-emp-btn--quiet" onClick={onClose} title="Закрыть">
                        <Icon d="M4 4l8 8M12 4l-8 8" />
                    </button>
                </div>
                <div className="pc-emp-mb">
                    <label className="pc-emp-fl" style={{ marginBottom: 14 }}>
                        <span>Название</span>
                        <input
                            className="pc-emp-inp"
                            value={name}
                            autoFocus
                            placeholder="Например, Nissan"
                            onChange={(event) => setName(event.target.value)}
                            onKeyDown={(event) => { if (event.key === 'Enter') handleSave(); }}
                        />
                    </label>
                    <div className="pc-emp-fl">
                        <span>Тип</span>
                        <div className="pc-company-types">
                            {CAMPAIGN_TYPES.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`pc-company-type${type === item.id ? ' is-on' : ''}`}
                                    onClick={() => setType(item.id)}
                                >
                                    <b>{item.name}</b>
                                    <i>{item.hint}</i>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="pc-emp-mf">
                    <button type="button" className="pc-emp-btn pc-emp-btn--pri" disabled={!canSave} onClick={handleSave}>
                        {saving ? 'Сохранение…' : 'Добавить'}
                    </button>
                    <button type="button" className="pc-emp-btn" onClick={onClose}>Закрыть</button>
                </div>
            </div>
        </div>
    );
};

const heatClass = (value) => {
    const n = Math.max(0, Math.min(7, parseInt(value, 10) || 0));
    return `pc-heat-${n}`;
};

const Campaign = ({i, campaigns, operators, queues }) => (
    <div className="pc-company">
        <section className="pc-sect">
            <div className="pc-sect__t">Расписание</div>
            <SchedulePanel campaigns={campaigns} rows={rows} i={i} />
        </section>
        <div className="pc-company-split">
            <section className="pc-sect">
                <div className="pc-sect__t">
                    Операторы
                    <span className="pc-emp-chip pc-emp-chip--mono">
                        {(campaigns && campaigns[i] && campaigns[i].operators ? campaigns[i].operators.length : 0)}
                    </span>
                </div>
                <Field
                    name={`campaigns[${i}].operators`}
                    component={AssignField}
                    kind="operators"
                    items={operators}
                />
            </section>
            <section className="pc-sect">
                <div className="pc-sect__t">
                    Очереди КЦ
                    <span className="pc-emp-chip pc-emp-chip--mono">
                        {(campaigns && campaigns[i] && campaigns[i].queues ? campaigns[i].queues.length : 0)}
                    </span>
                </div>
                <Field
                    name={`campaigns[${i}].queues`}
                    component={AssignField}
                    kind="queues"
                    items={queues}
                />
            </section>
        </div>
    </div>
)


const List = ({campaigns, setSelected, index}) => (
    <div>
        <table className="pc-company-list">
            <thead>
                <tr>
                    <th>Компания</th>
                </tr>
            </thead>
            <tbody>
                {(campaigns || []).map((row, i) => (
                    <tr
                        key={i}
                        onClick={() => setSelected(i)}
                        className={i === index ? 'cur' : ''}
                    >
                        <td>
                            <div className="pc-company-list__row">
                                <span className="nm">{row.name}</span>
                                <StatusChip tone={row.status === 'Disabled' ? 'danger' : 'ok'} dot>
                                    {row.status === 'Disabled' ? 'Заблок.' : 'Активна'}
                                </StatusChip>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        <EmptyState
            description="Компании делят проект по брендам: у каждой своё расписание, операторы и очереди КЦ."
        />
    </div>
);

const SchedulePanel = ({rows, i, campaigns}) => (
    <div>
        <div className="pc-legend">
            <span>Требуется операторов в час:</span>
            <span className="sc">
                <i className="pc-heat-0"></i>
                <i className="pc-heat-1"></i>
                <i className="pc-heat-2"></i>
                <i className="pc-heat-3"></i>
                <i className="pc-heat-4"></i>
                <i className="pc-heat-5"></i>
                <i className="pc-heat-6"></i>
                <i className="pc-heat-7"></i>
            </span>
            <span className="pc-mono">0 → 7</span>
        </div>
        <div className="pc-sched">
            <table>
                <thead>
                    <tr>
                        <th className="day">День</th>
                        <th className="tm">Смена</th>
                        {HOURS.map((hour) =>
                            <th key={hour}>{String(hour).padStart(2, '0')}</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, j) => (
                        <tr key={j}>
                            <td className={`day${j >= 5 ? ' we' : ''}`}>
                                {row}
                            </td>
                            <td className="tm">
                                <div className="tmwrap">
                                    <Field
                                        name={`campaigns[${i}].schedule[${j+1}].start`}
                                        required={true}
                                        component={SelectInput}
                                        options={SHIFT_TIMES}
                                        compact={true}
                                        style={{width: 88}}
                                    />
                                    <span>—</span>
                                    <Field
                                        name={`campaigns[${i}].schedule[${j+1}].end`}
                                        required={true}
                                        component={SelectInput}
                                        options={SHIFT_TIMES}
                                        compact={true}
                                        style={{width: 88}}
                                    />
                                </div>
                            </td>
                            {HOURS.map((hour) => {
                                const daySchedule = campaigns?.[i]?.schedule?.[j+1];
                                const dis = !hourOverlapsShift(hour, daySchedule);
                                const needed = daySchedule && daySchedule.needed ? daySchedule.needed[hour] : 0;
                                return <td key={hour} className={heatClass(needed)}>
                                        <Field
                                            style={{width: '100%'}}
                                            name={`campaigns[${i}].schedule[${j+1}].needed[${hour}]`}
                                            component={TextInput}
                                            disabled={dis}
                                        />
                                </td>
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
)

const CampaignsFunc = ({formValues, change, operators = [], queues = [], isSaving, invalid, ...props}) =>{
    const [selected, setSelected] = useState(0);
    const [newCapmaign, setNewCapmaign] = useState(false);
    if (!formValues) {
        return null;
    }
    const campaigns = formValues.campaigns || [];

    const selectCampaign = (i) => {
        setSelected(i)
    }

    const open = () => {
        setNewCapmaign(!newCapmaign);
    }

    const save = async (campaign) => {
        if(campaign){
            campaign.project = formValues._id;
            campaign.status = 'Active';
            campaign.operators = [];
            campaign.queues = [];
            campaign.schedule = [null].concat(rows.map(() => ({ start: 7 * 60, end: 24 * 60, needed: {} })));
            const { status, data } = await provider(
                CREATE,
                '/campaigns',
                { data: campaign }
            );
            if (status === 200 && data) {
                const next = [...campaigns, data];
                change('campaigns', next);
                await provider(
                    UPDATE,
                    '/projects',
                    {  id: formValues._id,
                        data: { ...formValues, campaigns: next }
                    }
                );
                setSelected(next.length - 1);
                setNewCapmaign(false);
            }
        }
    }
    

    const blockCampaign = async (index) => {
        change(`campaigns[${index}].status`, "Disabled");
    }

    const unBlockCampaign = async (index) => {
        change(`campaigns[${index}].status`, "Active");
    }

    const block = () => {
        if(!campaigns[selected]){
            return;
        }
        if(campaigns[selected].status === 'Active'){
            blockCampaign(selected);
        }else{
             unBlockCampaign(selected);
        }
    }

    return (
        <Fragment>
        <div className="pc-fill">
        <div className="pc-split pc-split--side">
            <CardElement
                fill
                action={<AddButton  handleAddClick={open} label="Добавить" variant="contained" />}
                content={
                    <div className="pc-panel-body">
                        <div className="pc-tblwrap">
                            <List campaigns={campaigns} setSelected={selectCampaign} index={selected}/>
                        </div>
                    </div>
                }
            />
            {campaigns&&campaigns.map((el, index) => (
                <Fragment key={index}>
                    {index===selected&&
                        <CardElement
                            fill
                            title={el.name}
                            subheader={scheduleSummary(el)
                                ? <StatusChip tone="mono">{scheduleSummary(el)}</StatusChip>
                                : <StatusChip tone={el.status === 'Active' ? 'ok' : 'danger'} dot>
                                    {el.status === 'Active' ? 'Активна' : 'Заблокирована'}
                                  </StatusChip>
                            }
                            action={<BlockButton handleBlockClick={block} status={el.status}/>}
                            content={
                                <div className="pc-tblwrap">
                                <Campaign
                                    el={el}
                                    i={index}
                                    isSaving={isSaving}
                                    invalid={invalid}
                                    operators={operators}
                                    queues={queues}
                                    campaigns={campaigns}
                                />
                                </div>
                            }
                        />
                    }
                </Fragment>
            ))}
        </div>
        </div>
            <CreateCompanyModal
                open={newCapmaign}
                onClose={() => setNewCapmaign(false)}
                onSave={save}
            />
        </Fragment>
    );

}

const mapStateToProps = (state, props) => {
    const formValues = getFormValues('project')(state);
    return { 
        formValues
    }
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'project',
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
        pure: false,
    })
);

export default enhance(CampaignsFunc);
